import React, {useEffect, useState} from "react"
import CardMedia from "@mui/material/CardMedia";
import {RichText} from "../../../../packages/graphql-layer/src";
import useWindowSize from "../../../utility/useWindowSize";
import CCOPlainText from "../../fundamentals/CCOPlainText";
import CCOComponentWrapper from "../../fundamentals/CCOComponentWrapper";
import Script from "next/script";
import Box from "@mui/material/Box";
import CCOContainer from "../../fundamentals/CCOContainer";
import {getUniqueKey} from "../../../utility/componentUtil";
import {CmVideoType} from "../../types";

export interface Props {
    video?: CmVideoType;
    youtubeUrl?: string;
    teaserText?: RichText;
    noContainer?: boolean;
    customStyle?: any;
    layoutSettings?: any;
}

function C041Video(props: Props) {
    const {greaterThanWindowMedium, greaterThanWindowLarge} = useWindowSize();
    const url = props.youtubeUrl ? props.youtubeUrl : (props.video?.dataUrl ?? "")

    const YOUTUBE_SCRIPT = "https://www.youtube.com/iframe_api";
    const USERCENTRICS_YOUTUBE_SERVICE_NAME = "YouTube Video";

    const [currentOrigin, setCurrentOrigin] = useState<string | undefined>(undefined);
    const uuids = getUniqueKey();

    const wrapperStyle = {
        position: 'relative',
        aspectRatio: '16/9',
    }

    const videoCartStyle = {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        boxSizing: 'content-box',
        padding: 0,
        borderStyle: "none",
    }

    const caption: JSX.Element = props.video?.detailText ?
        <Box sx={{
            marginTop: greaterThanWindowLarge ? 6 : 3,
            fontSize: '16px',
            '& .MuiTypography-root': {lineHeight: '150%'}
        }}>
            <CCOPlainText metadataId={props.video.id}
                          propertyName='properties.detailText'
                          textAsTree={props.video?.detailText?.textAsTree} variant="body2"/>
        </Box> : <></>

    useEffect(() => {
        if (typeof window !== "undefined") {
            setCurrentOrigin(window.location.origin);
        }
    }, []);

    function getYoutubeUrl(rawUrl: string) {
        const parsedUrl = new URL(rawUrl);
        const searchParams = parsedUrl.searchParams;

        // Remove all parameters except video-id 'v'
        searchParams.forEach((_value, key) => {
            if (key !== 'v') {
                searchParams.delete(key);
            }
        });

        const cleanedUrl = `${parsedUrl.origin}${parsedUrl.pathname}${parsedUrl.search}`;
        const originParam = `?showinfo=0&enablejsapi=1&origin=${currentOrigin}`;

        if (cleanedUrl && cleanedUrl.startsWith("https://www.youtube.com/embed/")) {
            return cleanedUrl + originParam;
        } else if (
            (cleanedUrl && cleanedUrl.startsWith("https://www.youtube.com/watch?v=")) ||
            cleanedUrl.startsWith("https://www.youtube.de/watch?v=")
        ) {
            return (
                "https://www.youtube.com/embed/" +
                cleanedUrl.substring(cleanedUrl.indexOf("?v=") + 3, cleanedUrl.length) +
                originParam
            );
        } else if (cleanedUrl.startsWith("https://youtu.be")) {
            return (
                "https://www.youtube.com/embed/" +
                cleanedUrl.substring(cleanedUrl.indexOf("be/") + 3, cleanedUrl.length) +
                originParam
            );
        } else {
            return '';
        }
    }

    const cardMedia = () => {
        const embedUrl = currentOrigin ? getYoutubeUrl(url) : "";
        return (
            <React.Fragment>
                <Box id={props.video?.id} sx={wrapperStyle}>
                    <CardMedia
                        sx={videoCartStyle}
                        component='iframe'
                        data-testid='iframe'
                        id='player'
                        datatype='text/html'
                        allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen'
                        data-src={embedUrl}
                        data-service={USERCENTRICS_YOUTUBE_SERVICE_NAME}
                    />
                </Box>
                {caption}
            </React.Fragment>
        );
    };

    if (url && currentOrigin) {
        const youtubeScriptTag = (
            <Script
                id="youtube-iframe-api"
                strategy="afterInteractive"
                type="text/plain"
                data-usercentrics={USERCENTRICS_YOUTUBE_SERVICE_NAME}
                src={YOUTUBE_SCRIPT}
            />
        );

        if (props.noContainer) {
            return (
                <Box sx={{marginBottom: '24px'}}>
                    {youtubeScriptTag}
                    {cardMedia()}
                </Box>
            );
        }


        return (
            <CCOComponentWrapper
                noBottomMargin={props.layoutSettings?.noBottomMargin && greaterThanWindowMedium}
                key={uuids[0]}
                customStyle={props.customStyle}
            >
                <CCOContainer>
                    {youtubeScriptTag}
                    {cardMedia()}
                </CCOContainer>
            </CCOComponentWrapper>
        );
    } else {
        return <React.Fragment key={uuids[0]}/>;
    }
}

export default C041Video;
